import { useMemo, type JSX, type ReactNode } from 'react';
import type { RefrigerationUnit } from '@/data/schemas';
import type { SafetyFieldConfig, SafetySectionDefinition } from '../config/schemas/types';
import { DataList, Panel } from '@/components/blocks/data-display';
import { Section } from '@/components/blocks/layout';
import { SectionHeader } from '@/components/blocks/page';
import {
  generateSafetyDisplaySections,
  defaultSafetyFormatters,
  getNestedValue,
} from '../utils/safetyDisplayHelpers';
import { EmbeddedArrayTable } from './EmbeddedArrayTable';

export interface SafetySubTabViewProps {
  /** The refrigeration unit data */
  unit: RefrigerationUnit;
  /** Field definitions for this sub-tab */
  fields: readonly SafetyFieldConfig[];
  /** Section definitions with titles and order */
  sections: Record<string, SafetySectionDefinition>;
  /** Section header title */
  title: string;
  /** Callback when edit button is clicked */
  onEdit: () => void;
  /** Callback to update an embedded array field */
  onUpdateArray?: (dataPath: string, items: unknown[]) => Promise<void>;
  /** Whether an array update is in progress */
  isUpdating?: boolean;
  /** Catalog ID to label maps for resolving multi-select values in tables */
  catalogLabelMaps?: Record<string, Map<string, string>>;
  /** Optional children for manual sections */
  children?: ReactNode;
}

/**
 * Schema-driven view component for safety sub-tabs.
 *
 * Generates Panel grid from field schema definitions, with support for:
 * - Nested data paths (e.g., "technical.safety.detectors.toxicCount")
 * - Unit suffixes for numbers
 * - Panel grouping
 *
 * Manual sections (like ThresholdTable) can be passed as children.
 *
 * @example
 * ```tsx
 * <SafetySubTabView
 *   unit={unit}
 *   fields={rilevazioneFields}
 *   sections={rilevazioneSections}
 *   title="Sistemi di rilevazione"
 *   onEdit={() => setModalOpen(true)}
 * >
 *   <ThresholdTable thresholds={...} />
 * </SafetySubTabView>
 * ```
 */
export function SafetySubTabView({
  unit,
  fields,
  sections: sectionDefs,
  title,
  onEdit,
  onUpdateArray,
  isUpdating = false,
  catalogLabelMaps,
  children,
}: SafetySubTabViewProps): JSX.Element {
  // Split fields into scalar (no arrayConfig) and array (with arrayConfig)
  const { scalarFields, arrayFields } = useMemo(() => {
    const scalar: SafetyFieldConfig[] = [];
    const array: SafetyFieldConfig[] = [];
    for (const field of fields) {
      if (field.arrayConfig) {
        array.push(field);
      } else {
        scalar.push(field);
      }
    }
    return { scalarFields: scalar, arrayFields: array };
  }, [fields]);

  // Generate display sections from scalar fields only
  const displaySections = generateSafetyDisplaySections(
    unit as unknown as Record<string, unknown>,
    scalarFields,
    sectionDefs,
    defaultSafetyFormatters,
    catalogLabelMaps
  );

  return (
    <Section spacing="md">
      <SectionHeader title={title} action={{ label: 'Modifica', onClick: onEdit }} />

      {/* Scalar fields in Panel grid */}
      <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {displaySections.map((section) => (
          <Panel key={section.key} title={section.title}>
            <DataList items={section.items} />
          </Panel>
        ))}
      </div>

      {/* Array fields as embedded tables */}
      {arrayFields.map((field) => {
        const config = field.arrayConfig!;
        const dataPath = field.dataPath ?? field.key;
        const items =
          getNestedValue<Array<{ id: string }>>(
            unit as unknown as Record<string, unknown>,
            dataPath
          ) ?? [];

        return (
          <EmbeddedArrayTable
            key={field.key}
            title={config.tableTitle ?? field.label}
            items={items}
            itemFields={config.itemFields}
            addLabel={config.addLabel}
            emptyMessage={config.emptyMessage}
            modalTitle={config.modalTitle}
            defaultValues={config.defaultValues as Partial<{ id: string }>}
            onUpdate={(updatedItems) =>
              onUpdateArray?.(dataPath, updatedItems) ?? Promise.resolve()
            }
            isUpdating={isUpdating}
            catalogLabelMaps={catalogLabelMaps}
          />
        );
      })}

      {children}
    </Section>
  );
}
